import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router';
import { PageLoader } from '@/components/ui/Feedback';
import AboutPage from '@/pages/AboutPage';
import CatalogPage from '@/pages/CatalogPage';
import HomePage from '@/pages/HomePage';
import NotFoundPage from '@/pages/NotFoundPage';
import ProductPage from '@/pages/ProductPage';
import { ContactPage, PrivacyPolicyPage, PrivateLabelPage, TermsPage } from '@/pages/StaticPages';
import { PublicShell } from './PublicShell';
import { RouteError } from './RouteError';

// The admin portal is code-split so anonymous visitors never download it.
const AdminLoginPage = lazy(() => import('@/features/admin/LoginPage'));
const AdminShell = lazy(() => import('@/features/admin/AdminShell'));
const DashboardPage = lazy(() => import('@/features/admin/DashboardPage'));
const InquiriesPage = lazy(() => import('@/features/admin/InquiriesPage'));
const InquiryDetailPage = lazy(() => import('@/features/admin/InquiryDetailPage'));
const ProductsPage = lazy(() => import('@/features/admin/ProductsPage'));
const ProductEditPage = lazy(() => import('@/features/admin/ProductEditPage'));
const CategoriesPage = lazy(() => import('@/features/admin/CategoriesPage'));
const ContentPage = lazy(() => import('@/features/admin/ContentPage'));
const SettingsPage = lazy(() => import('@/features/admin/SettingsPage'));
const AuditLogsPage = lazy(() => import('@/features/admin/AuditLogsPage'));
const AccountPage = lazy(() => import('@/features/admin/AccountPage'));

function load(Component: LazyExoticComponent<ComponentType>) {
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
}

export const routes: RouteObject[] = [
  {
    element: <PublicShell />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'about', element: <AboutPage /> },
      { path: 'apparel', element: <CatalogPage key="apparel" division="APPAREL" /> },
      { path: 'apparel/:categorySlug', element: <CatalogPage key="apparel-cat" division="APPAREL" /> },
      { path: 'sports-goods', element: <CatalogPage key="sports" division="SPORTS" /> },
      { path: 'sports-goods/:categorySlug', element: <CatalogPage key="sports-cat" division="SPORTS" /> },
      { path: 'products/:slug', element: <ProductPage /> },
      { path: 'private-label', element: <PrivateLabelPage /> },
      { path: 'contact', element: <ContactPage /> },
      { path: 'privacy-policy', element: <PrivacyPolicyPage /> },
      { path: 'terms', element: <TermsPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  { path: '/admin/login', element: load(AdminLoginPage), errorElement: <RouteError /> },
  {
    path: '/admin',
    element: load(AdminShell),
    errorElement: <RouteError />,
    children: [
      { index: true, element: load(DashboardPage) },
      { path: 'inquiries', element: load(InquiriesPage) },
      { path: 'inquiries/:id', element: load(InquiryDetailPage) },
      { path: 'products', element: load(ProductsPage) },
      { path: 'products/new', element: load(ProductEditPage) },
      { path: 'products/:id', element: load(ProductEditPage) },
      { path: 'categories', element: load(CategoriesPage) },
      { path: 'content', element: load(ContentPage) },
      { path: 'settings', element: load(SettingsPage) },
      { path: 'audit-logs', element: load(AuditLogsPage) },
      { path: 'account', element: load(AccountPage) },
    ],
  },
];

export function createAppRouter() {
  return createBrowserRouter(routes);
}
